import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AppBar,
  Toolbar,
  Typography,
  Modal,
  Snackbar,
  Alert,
  InputAdornment,
  Container,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { Navigate, useNavigate } from "react-router-dom";

const Purpose = () => {
  let navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPurposeValue, setEditPurposeValue] = useState("");
  const [currentPurposeID, setCurrentPurposeID] = useState(null);
  const [newPurposeValue, setNewPurposeValue] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [purposes, setPurposes] = useState([]);
  const [purposeToDelete, setPurposeToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPurposes, setFilteredPurposes] = useState([]);
  const [permissions, setPermissions] = useState({
    uRead: 0,
    uWrite: 0,
    uModify: 0,
    uDelete: 0,
    isPermission: 0,
  });
  const [formId, setFormId] = useState(null);
  const [loading, setLoading] = useState(true); // For permission loading
  const [purposeLoading, setPurposeLoading] = useState(false); // For purpose data loading
  const [error, setError] = useState(null); // New error state

  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch Form ID
  const fetchFormId = async () => {
    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/ExclusivePermission/Form_Authorizer",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ moduleId: 7 }),
        }
      );
      const data = await response.json();
      console.log("Form Authorizer Response:", data); // Debug log
      if (response.ok) {
        const purposeForm = data.find((form) => form.formName === "Purpose");
        if (purposeForm) {
          setFormId(purposeForm.formId);
          console.log("Form ID set:", purposeForm.formId); // Debug log
        } else {
          setError("Purpose form not found in response");
          setLoading(false);
        }
      } else {
        setError("Failed to fetch form ID");
        setSnackbar({
          open: true,
          message: "Failed to fetch form ID",
          severity: "error",
        });
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching form ID:", error); // Debug log
      setError("Error fetching form ID");
      setSnackbar({
        open: true,
        message: "Error fetching form ID",
        severity: "error",
      });
      setLoading(false);
    }
  };

  // Fetch Permissions
  const fetchPermissions = async () => {
    if (!formId) return;
    try {
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId }),
      });
      const data = await response.json();
      console.log("Module Authorizer Response:", data); // Debug log
      if (response.ok && data.length > 0) {
        setPermissions(data[0]);
        console.log("Permissions set:", data[0]); // Debug log
      } else {
        setPermissions({ ...permissions, isPermission: 0 });
        setError("Failed to fetch permissions");
        setSnackbar({
          open: true,
          message: "Failed to fetch permissions",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error); // Debug log
      setError("Error fetching permissions");
      setSnackbar({
        open: true,
        message: "Error fetching permissions",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPurposes = async () => {
    setPurposeLoading(true); // Start purpose loading
    try {
      const response = await fetch(`${apiUrl}/Enquiry/ddlPurpose`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });
      const data = await response.json();
      console.log("Purposes Response:", data); // Debug log
      if (response.ok) {
        setPurposes(data);
        setFilteredPurposes(data);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to fetch purposes",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching purposes:", error); // Debug log
      setSnackbar({
        open: true,
        message: "Error fetching data",
        severity: "error",
      });
    } finally {
      setPurposeLoading(false); // End purpose loading
    }
  };

  useEffect(() => {
    fetchFormId();
  }, []);

  useEffect(() => {
    if (formId) {
      fetchPermissions();
    }
  }, [formId]);

  useEffect(() => {
    if (permissions.isPermission && permissions.uRead) {
      fetchPurposes();
    }
  }, [permissions]);

  useEffect(() => {
    setFilteredPurposes(
      purposes.filter((purpose) =>
        purpose.purpose.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, purposes]);

  const handleClose = () => {
    setShowModal(false);
  };

  const handleAddNewPurpose = () => {
    navigate("/AddPurpose");
  };

  const handleEditPurpose = (purposeName, purposeID) => {
    navigate(`/EditPurpose/${purposeID}`, {
      state: { purposeName, purposeId: purposeID },
    });
  };

  const handleCloseModal = () => {
    setAddModalOpen(false);
    setIsEditModalOpen(false);
    setNewPurposeValue("");
    setEditPurposeValue("");
    setCurrentPurposeID(null);
  };

  const handleAddPurpose = async () => {
    try {
      const response = await fetch(`${apiUrl}/Enquiry/CreatePurpose`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ purpose: newPurposeValue }),
      });
      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Purpose added successfully!",
          severity: "success",
        });
        fetchPurposes();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to add purpose",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error adding purpose:", error); // Debug log
      setSnackbar({
        open: true,
        message: "Error adding purpose",
        severity: "error",
      });
    } finally {
      setAddModalOpen(false);
      setNewPurposeValue("");
    }
  };

  const handleUpdatePurpose = async () => {
    if (!editPurposeValue.trim()) {
      setSnackbar({
        open: true,
        message: "Purpose name cannot be empty.",
        severity: "error",
      });
      return;
    }
    try {
      const response = await fetch(
        `${apiUrl}/Enquiry/UpdatePurpose/${currentPurposeID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ purpose: editPurposeValue }),
        }
      );
      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Purpose updated successfully!",
          severity: "success",
        });
        fetchPurposes();
        setIsEditModalOpen(false);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to update purpose",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating purpose:", error); // Debug log
      setSnackbar({
        open: true,
        message: "Error updating purpose",
        severity: "error",
      });
    }
  };

  const handleDeletePurpose = async () => {
    try {
      const response = await fetch(
        `${apiUrl}/Enquiry/DeletePurpose/${purposeToDelete}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Purpose deleted successfully!",
          severity: "success",
        });
        fetchPurposes();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to delete purpose",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting purpose:", error); // Debug log
      setSnackbar({
        open: true,
        message: "Error deleting purpose",
        severity: "error",
      });
    }
    setShowModal(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "" });
  };

  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error || !permissions.isPermission) {
    return (
      <Container>
        <Typography
          variant="h4"
          color="error"
          sx={{ mt: 4, textAlign: "center" }}
        >
          You are not authorised to view this page
        </Typography>
      </Container>
    );
  }

  const handleChangePage = (event, newPage) => {
  setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
  setRowsPerPage(parseInt(event.target.value, 10));
  setPage(1); // reset to page 1
};

const paginatedData = filteredPurposes.slice(
  (page - 1) * rowsPerPage,
  page * rowsPerPage
);


  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography
            variant="h4"
            component="div"
            style={{ fontWeight: "bold" }}
          >
            Purpose
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper
        elevation={3}
        style={{ padding: 16, width: "100%", margin: "auto", marginTop: 6 }}
      >
        <Box
          sx={{
            marginBottom: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: { padding: "0px 8px" },
            }}
            sx={{
              width: 300,
              backgroundColor: "#f9f9f9",
            }}
          />
          <Tooltip
            title={
              permissions.uWrite ? "" : "You are not authorised to add purposes"
            }
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                style={{
                  width: "160px",
                  height: "50px",
                  padding: "10px 0",
                  fontWeight: "bold",
                }}
                onClick={handleAddNewPurpose}
                disabled={!permissions.uWrite}
              >
                ADD NEW PURPOSE
              </Button>
            </span>
          </Tooltip>
        </Box>
        {permissions.uRead ? (
          <TableContainer component={Paper}>
            <Table
              sx={{
                minWidth: 650,
                border: "1px solid #ccc",
                "& .MuiTableCell-root": {
                  borderRight: "1px solid #ccc",
                  fontSize: "1rem",
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  fontWeight: "bold",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #ccc",
                      borderBottom: "1px solid #ccc",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Purpose
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purposeLoading ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPurposes.length > 0 ? (
                  paginatedData.map((purpose, index) => (
                    <TableRow key={index} sx={{ borderTop: "1px solid #ccc" }}>
                      <TableCell
                        sx={{
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        {purpose.purpose}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        <Tooltip
                          title={
                            permissions.uModify
                              ? ""
                              : "You are not authorised to edit purposes"
                          }
                        >
                          <span>
                            <Button
                              variant="contained"
                              color="warning"
                              style={{ marginLeft: "10px" }}
                              onClick={() =>
                                handleEditPurpose(
                                  purpose.purpose,
                                  purpose.purposeId
                                )
                              }
                              startIcon={<EditIcon />}
                              disabled={!permissions.uModify}
                            >
                              EDIT
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            permissions.uDelete
                              ? ""
                              : "You are not authorised to delete purposes"
                          }
                        >
                          <span>
                            <Button
                              className="mx-2"
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setPurposeToDelete(purpose.purposeId);
                                setShowModal(true);
                              }}
                              startIcon={<DeleteIcon />}
                              disabled={!permissions.uDelete}
                            >
                              DELETE
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No Purposes Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>
            You are not authorised to view purposes
          </Typography>
        )}
        {filteredPurposes.length > 0 && (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
    <TextField
      select
      label="Rows per page"
      value={rowsPerPage}
      onChange={handleChangeRowsPerPage}
      sx={{ width: 150 }}
      SelectProps={{
        native: true,
      }}
    >
      {rowsPerPageOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </TextField>

    <Box>
      <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
        Page {page} of {Math.ceil(filteredPurposes.length / rowsPerPage)}
      </Typography>
      <Button
        variant="outlined"
        onClick={() => handleChangePage(null, page - 1)}
        disabled={page === 1}
        sx={{ mr: 1 }}
      >
        Prev
      </Button>
      <Button
        variant="outlined"
        onClick={() => handleChangePage(null, page + 1)}
        disabled={page >= Math.ceil(filteredPurposes.length / rowsPerPage)}
      >
        Next
      </Button>
    </Box>
  </Box>
)}

      </Paper>

      <Modal
        open={showModal}
        onClose={handleClose}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 16px" }}>
            <Typography
              id="delete-modal-title"
              variant="h6"
              sx={{ color: "#fff" }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: "20px" }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete this purpose?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeletePurpose}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Purpose;
